import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiCall } from "../lib/api";
import { 
  DollarSign, CreditCard, CheckCircle, Clock, AlertCircle, 
  GraduationCap, FileText, BookOpen, Users, Award, Calendar,
  ArrowRight, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";

export const FeesPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [feePackages, setFeePackages] = useState({});
  const [studentFees, setStudentFees] = useState(null);
  const [children, setChildren] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(searchParams.get("student") || null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/portal");
      return;
    }
    fetchFeePackages();
    
    if (user.user_type === "parent") {
      fetchChildren();
    } else if (user.user_type === "student") {
      setSelectedStudent(user.id);
      fetchStudentFees(user.id);
    }
  }, [user, navigate]);

  useEffect(() => {
    if (selectedStudent) {
      fetchStudentFees(selectedStudent);
    }
  }, [selectedStudent]);

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchFeePackages = async () => {
    try {
      const data = await apiCall("GET", "/fees/packages", null, token);
      setFeePackages(data);
    } catch (err) {
      console.error("Failed to fetch fee packages:", err);
    }
  };

  const fetchChildren = async () => {
    try {
      const data = await apiCall("GET", "/parent/children", null, token);
      setChildren(data || []);
      if (data && data.length > 0 && !selectedStudent) {
        setSelectedStudent(data[0].child_id);
      }
    } catch (err) {
      console.error("Failed to fetch children:", err);
    }
    setLoading(false);
  };

  const fetchStudentFees = async (studentId) => {
    try {
      const data = await apiCall("GET", `/fees/student/${studentId}`, null, token);
      setStudentFees(data);
    } catch (err) {
      console.error("Failed to fetch student fees:", err);
    }
    setLoading(false);
  };

  const handlePayFee = async (feeType) => {
    if (!selectedStudent) {
      toast.error("Please select a student first");
      return;
    }
    
    setProcessingPayment(feeType);
    try {
      const originUrl = window.location.origin;
      const response = await apiCall("POST", "/fees/checkout", {
        fee_type: feeType,
        student_id: selectedStudent,
        origin_url: originUrl
      }, token);
      
      // Redirect to Stripe checkout
      window.location.href = response.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setProcessingPayment(null);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingStatus(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const pollStatus = async () => {
      try {
        const status = await apiCall("GET", `/fees/checkout/status/${sessionId}`, null, token);
        
        if (status.payment_status === "paid") {
          toast.success("Payment successful! Thank you for your payment.");
          // Clear URL params and refresh fees
          navigate("/fees", { replace: true });
          if (selectedStudent) {
            fetchStudentFees(selectedStudent);
          }
          setCheckingStatus(false);
          return;
        } else if (status.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          navigate("/fees", { replace: true });
          setCheckingStatus(false);
          return;
        }
        
        // Continue polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          toast.info("Payment is being processed. Please check your payment history.");
          navigate("/fees", { replace: true });
          setCheckingStatus(false);
        }
      } catch (err) {
        console.error("Error checking payment status:", err);
        setCheckingStatus(false);
      }
    };
    
    pollStatus();
  };

  const getFeeIcon = (feeType) => {
    const icons = {
      tuition: GraduationCap,
      books: BookOpen,
      activities: Award,
      examination: FileText,
      pta: Users,
      full_term: Calendar
    };
    return icons[feeType] || DollarSign;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid": return "bg-green-100 text-green-700";
      case "pending": return "bg-yellow-100 text-yellow-700";
      case "overdue": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-yellow-400 to-orange-400">
        <div className="spinner"></div>
      </div>
    );
  }

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-yellow-400 to-orange-400">
        <Card className="w-96 text-center">
          <CardContent className="p-8">
            <Loader2 className="mx-auto mb-4 animate-spin text-[#FF6B00]" size={48} />
            <h3 className="text-xl font-semibold text-[#002147] mb-2">Processing Payment</h3>
            <p className="text-gray-500">Please wait while we verify your payment...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="fees-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-[#002147] rounded-full flex items-center justify-center">
              <DollarSign className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#002147]">School Fees</h1>
              <p className="text-[#002147]/70">Pay and manage school fees securely</p>
            </div>
          </div>
        </div>

        {/* Student Selector (for parents) */}
        {user?.user_type === "parent" && children.length > 0 && (
          <Card className="mb-6 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <label className="font-medium text-[#002147]">Select Child:</label>
                <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                  <SelectTrigger className="w-64" data-testid="student-selector">
                    <SelectValue placeholder="Select a child" />
                  </SelectTrigger>
                  <SelectContent>
                    {children.map((child) => (
                      <SelectItem key={child.child_id} value={child.child_id}>
                        {child.child_name} ({child.child_grade || "N/A"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No children message for parents */}
        {user?.user_type === "parent" && children.length === 0 && (
          <Card className="mb-6 bg-white">
            <CardContent className="p-8 text-center">
              <Users size={48} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-[#002147] mb-2">No Children Linked</h3>
              <p className="text-gray-500 mb-4">Link your children to your account to pay their fees</p>
              <Button onClick={() => navigate("/parent-portal")} className="bg-[#FF6B00] hover:bg-[#E65C00]">
                Go to Parent Portal
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Fee Summary */}
        {studentFees && (
          <>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Due</p>
                      <p className="text-3xl font-bold text-red-600">${studentFees.total_due.toFixed(2)}</p>
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertCircle className="text-red-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Paid</p>
                      <p className="text-3xl font-bold text-green-600">${studentFees.total_paid.toFixed(2)}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="text-green-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Payment Progress</p>
                      <p className="text-3xl font-bold text-[#002147]">
                        {((studentFees.total_paid / (studentFees.total_due + studentFees.total_paid)) * 100 || 0).toFixed(0)}%
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <CreditCard className="text-blue-600" size={24} />
                    </div>
                  </div>
                  <Progress 
                    value={(studentFees.total_paid / (studentFees.total_due + studentFees.total_paid)) * 100 || 0} 
                    className="mt-4" 
                  />
                </CardContent>
              </Card>
            </div>

            {/* Fee Items */}
            <Card className="mb-8 bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText size={20} /> Fee Breakdown
                </CardTitle>
                <CardDescription>Click "Pay Now" to make a payment</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {studentFees.fees.map((fee) => {
                    const IconComponent = getFeeIcon(fee.id);
                    return (
                      <Card key={fee.id} className={`${fee.status === "paid" ? "bg-green-50" : "bg-white"} border`}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              fee.status === "paid" ? "bg-green-100" : "bg-orange-100"
                            }`}>
                              <IconComponent size={20} className={fee.status === "paid" ? "text-green-600" : "text-orange-600"} />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-[#002147]">{fee.name}</h4>
                              <p className="text-sm text-gray-500">{fee.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-2xl font-bold text-[#002147]">${fee.amount.toFixed(2)}</p>
                              <Badge className={getStatusColor(fee.status)}>{fee.status}</Badge>
                            </div>
                            {fee.status !== "paid" && (
                              <Button 
                                className="bg-[#FF6B00] hover:bg-[#E65C00]"
                                onClick={() => handlePayFee(fee.id)}
                                disabled={processingPayment === fee.id}
                                data-testid={`pay-${fee.id}-btn`}
                              >
                                {processingPayment === fee.id ? (
                                  <Loader2 className="animate-spin mr-2" size={16} />
                                ) : (
                                  <CreditCard className="mr-2" size={16} />
                                )}
                                Pay Now
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Payment History */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock size={20} /> Payment History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {studentFees.payment_history?.length > 0 ? (
                    <div className="space-y-3">
                      {studentFees.payment_history.map((payment, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {payment.payment_status === "paid" ? (
                              <CheckCircle className="text-green-500" size={20} />
                            ) : payment.payment_status === "initiated" ? (
                              <Clock className="text-yellow-500" size={20} />
                            ) : (
                              <AlertCircle className="text-red-500" size={20} />
                            )}
                            <div>
                              <p className="font-medium">{feePackages[payment.fee_type]?.name || payment.fee_type}</p>
                              <p className="text-sm text-gray-500">
                                {new Date(payment.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-[#002147]">${payment.amount.toFixed(2)}</p>
                            <Badge variant="outline" className={
                              payment.payment_status === "paid" ? "text-green-600" : 
                              payment.payment_status === "initiated" ? "text-yellow-600" : "text-red-600"
                            }>
                              {payment.payment_status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Clock size={48} className="mx-auto mb-4 text-gray-300" />
                      <p>No payment history yet</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </>
        )}

        {/* No student selected */}
        {!studentFees && selectedStudent === null && user?.user_type !== "parent" && (
          <Card className="bg-white">
            <CardContent className="p-8 text-center">
              <DollarSign size={48} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-[#002147] mb-2">No Fee Information</h3>
              <p className="text-gray-500">Unable to load fee information. Please try again later.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FeesPage;
